import React from "react";
import _ from "lodash";
import { faPlusSquare, faMinusSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select, MenuItem, TextField, Tooltip } from "@material-ui/core";

import { removeItemFromArray } from "utils/utils";
import { normalizedLikertInputsConfig, LikertInputTypes } from "utils/questionConfig/likert"
import PropCheckbox from "../../../components/SurveyCreation/PropCheckbox";

export default class LikertConfig extends React.Component {
  /**
   * ⚠️ IMPORTANT: LOCAL BUFFERING ARCHITECTURE ⚠️
   *
   * This component DOES NOT auto-save every change like other components.
   * Instead, it buffers all custom value edits locally in state and ONLY
   * propagates to parent when validation passes.
   *
   * WHY: Allows users to freely edit custom Likert values (even temporarily
   * creating duplicates or empty values) without those invalid states being
   * saved to the backend. Invalid data stays buffered locally until fixed.
   *
   * HOW IT WORKS:
   * - Custom value edits → buffered in local state, marked as isDirty
   * - Validation runs on every change
   * - If valid → propagates to parent (auto-saves)
   * - If invalid → stays in buffer, shows error UI
   * - Structural changes (inputType, labels) → discard buffer, rebuild from props
   *
   * RESULT: Invalid Likert configurations can NEVER be saved through any path.
   */
  constructor(props) {
    super(props);
    this.state = {
      validationErrors: {}, // Tracks validation errors by index
      configData: null, // Buffers changes locally
      configSelectionIdx: 0,
      isDirty: false, // Tracks if we have unsaved valid changes
      questionId: props.id // Track question ID to detect question changes
    };
  }

  componentDidMount() {
    // Initialize from props
    this.initializeFromProps();
  }

  componentDidUpdate(prevProps, prevState) {
    // When switching to a different question, discard any buffered data and reinitialize
    // This prevents buffered invalid data from one question bleeding into another
    if (this.props.id !== this.state.questionId) {
      this.setState({ questionId: this.props.id }, () => {
        this.initializeFromProps();
      });
      return;
    }

    // Re-validate when config selection changes
    if (prevState.configSelectionIdx !== this.state.configSelectionIdx) {
      this.validateAllCustomValues();
    }

    // Reinitialize if props change externally (e.g., switching questions)
    const prevOptionsMatrix = prevProps.getQInfoIfMissingState('optionsMatrix') || {};
    const currentOptionsMatrix = this.props.getQInfoIfMissingState('optionsMatrix') || {};

    // Check if external changes occurred (not from our own updates)
    if (!this.state.isDirty &&
        JSON.stringify(prevOptionsMatrix) !== JSON.stringify(currentOptionsMatrix)) {
      this.initializeFromProps();
    }
  }

  initializeFromProps = () => {
    const options = this.props.getQInfoIfMissingState('options') || [];
    const optionsMatrix = this.props.getQInfoIfMissingState('optionsMatrix') || {};

    const inputType = optionsMatrix.inputType || 'RADIO';
    const inputs = normalizedLikertInputsConfig(optionsMatrix, options) || [];
    const labels = optionsMatrix.labels || { position: 'none' };

    let configSelectionIdx = this.state.configSelectionIdx;
    if (this.state.configData && this.state.configData.inputType !== inputType) {
      configSelectionIdx = 0;
    }

    // Ensure configSelectionIdx is within bounds
    if (configSelectionIdx >= inputs.length) {
      configSelectionIdx = 0;
    }

    this.setState({
      configSelectionIdx: configSelectionIdx,
      options: options,
      optionsMatrix: optionsMatrix,
      configData: {
        inputType: inputType,
        inputs: inputs,
        labels: labels
      },
      isDirty: false
    }, () => {
      this.validateAllCustomValues();
    });
  }

  validateAllCustomValues = () => {
    const { configSelectionIdx, configData } = this.state;
    if (configData &&
        configData.inputs &&
        configData.inputs[configSelectionIdx] &&
        configData.inputs[configSelectionIdx].isCustom &&
        configData.inputs[configSelectionIdx].custom) {
      this.validateCustomValues(configData.inputs[configSelectionIdx].custom, configSelectionIdx);
    }
  }

  validateCustomValues(customItems, currentConfigIdx) {
    const errors = {};
    const valueCount = {};

    // Count occurrences of each value
    customItems.forEach((item, index) => {
      const value = item.value;

      // Check for empty values
      if (value === "" || value === null || value === undefined) {
        errors[index] = "Empty value - won't be saved";
      } else {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          errors[index] = "Must be a number - won't be saved";
        } else {
          valueCount[numValue] = (valueCount[numValue] || 0) + 1;
        }
      }
    });

    // Mark duplicates
    customItems.forEach((item, index) => {
      if (item.value !== "" && item.value !== null && item.value !== undefined) {
        const numValue = Number(item.value);
        if (!isNaN(numValue) && valueCount[numValue] > 1) {
          errors[index] = errors[index] || `Duplicate value ${numValue} - won't be saved`;
        }
      }
    });

    // Update validation state
    this.setState(prevState => ({
      validationErrors: {
        ...prevState.validationErrors,
        [currentConfigIdx]: errors
      }
    }));

    const hasErrors = Object.keys(errors).length > 0;

    // Only propagate to parent if validation passes
    if (!hasErrors && this.state.isDirty) {
      this.propagateToParent();
    }

    return errors;
  }

  propagateToParent = () => {
    const { configData } = this.state;
    if (configData) {
      this.props.updateQInfo({ optionsMatrix: { ...configData } }, true);
      this.setState({ isDirty: false });
    }
  }

  // Helper: Build configData from props (used when discarding buffered changes)
  buildConfigDataFromProps = (overrides = {}) => {
    const options = this.props.getQInfoIfMissingState('options') || [];
    const optionsMatrix = this.props.getQInfoIfMissingState('optionsMatrix') || {};

    const inputType = overrides.inputType || optionsMatrix.inputType || 'RADIO';
    const inputs = normalizedLikertInputsConfig({ ...optionsMatrix, ...overrides }, options) || [];
    const labels = overrides.labels || optionsMatrix.labels || { position: 'none' };

    return {
      inputType,
      inputs,
      labels
    };
  }

  // Helper: Clone configData with new inputs (preserves other fields)
  cloneConfigData = () => {
    const { configData } = this.state;
    return { ...configData, inputs: _.cloneDeep(configData.inputs) };
  }

  // Helper: Update buffered state and validate
  updateBufferedState = (newConfigData, callback) => {
    const { configSelectionIdx } = this.state;
    this.setState({ configData: newConfigData, isDirty: true }, () => {
      this.validateCustomValues(newConfigData.inputs[configSelectionIdx].custom, configSelectionIdx);
      if (callback) callback();
    });
  }

  // Helper: Save immediately and clear buffer
  saveAndClearBuffer = (newConfigData) => {
    this.setState({
      configData: newConfigData,
      isDirty: false,
      validationErrors: {}
    }, () => {
      this.props.updateQInfo({ optionsMatrix: { ...newConfigData } }, true);
    });
  }

  // Helper: Generate unique value for new custom option
  generateUniqueValue = (existingCustomItems) => {
    const existingValues = new Set(existingCustomItems.map(item => Number(item.value)));
    let newValue = existingCustomItems.length + 1;

    while (existingValues.has(newValue)) {
      newValue++;
    }

    return newValue;
  }

  render() {
    const { configSelectionIdx, configData } = this.state;

    if (!configData || !configData.inputs || !configData.inputs[configSelectionIdx]) {
      return <div>Loading configuration...</div>;
    }

    const currentErrors = this.state.validationErrors[configSelectionIdx] || {};
    const hasErrors = Object.keys(currentErrors).length > 0;

    return(
      <>
        {
          configData.inputType && LikertInputTypes[configData.inputType] &&
          LikertInputTypes[configData.inputType].optionValuesConfig &&
          LikertInputTypes[configData.inputType].optionValuesConfig.indexOf('custom') !== -1 && (
            <div>
              <PropCheckbox
                label={{ text: "Enable Custom Values", placement: "right" }}
                update={(e) => {
                  const isChecked = e.target.checked;

                  let cData;
                  if (!isChecked) {
                    // Disabling: rebuild from props to discard any invalid buffered data
                    cData = this.buildConfigDataFromProps();
                    cData.inputs[configSelectionIdx].isCustom = false;
                  } else {
                    // Enabling: use buffered state, add defaults if needed
                    cData = this.cloneConfigData();
                    cData.inputs[configSelectionIdx].isCustom = true;

                    if (cData.inputs[configSelectionIdx].custom.length === 0) {
                      cData.inputs[configSelectionIdx].custom.push(
                        { label: "Option 1", value: 1 },
                        { label: "Option 2", value: 2 }
                      );
                    }
                  }

                  // Save immediately and clear validation errors when disabling
                  this.setState({
                    configData: cData,
                    isDirty: false,
                    validationErrors: isChecked ? this.state.validationErrors : {}
                  }, () => {
                    this.props.updateQInfo({ optionsMatrix: { ...cData } }, true);
                    if (isChecked) {
                      this.validateCustomValues(cData.inputs[configSelectionIdx].custom, configSelectionIdx);
                    }
                  });
                }}
                value={configData.inputs[configSelectionIdx].isCustom}
                disabled={false}
              />
            </div>
          )
        }
        
        {
          configData.inputType && LikertInputTypes[configData.inputType] &&
          LikertInputTypes[configData.inputType].optionalLabels && (
            <>
              <PropCheckbox
                label={{ text: "Display Slider Labels", placement: "right" }}
                update={(e) => {
                  const position = e.target.checked ? 'left' : 'none';
                  const newConfigData = this.buildConfigDataFromProps({ labels: { position } });
                  this.saveAndClearBuffer(newConfigData);
                }}
                value={configData.labels.position === 'left'}
                disabled={false}
              />
            </>
          )
        }
        <br />
        <Select
          fullWidth
          variant="outlined"
          value={configData.inputType}
          onChange={(e) => {
            const newConfigData = this.buildConfigDataFromProps({ inputType: e.target.value });
            this.saveAndClearBuffer(newConfigData);
          }}
        >
          {
            Object.keys(LikertInputTypes).map((type, idx) => (
              <MenuItem value={type} key={idx}>{ Object.values(LikertInputTypes)[idx].description }</MenuItem>
            ))
          }
        </Select><br/><br/>
        {
          configData.inputType && LikertInputTypes[configData.inputType] &&
          LikertInputTypes[configData.inputType].configByOption && (
            <>
              <Select
                fullWidth
                variant="outlined"
                value={configSelectionIdx}
                onChange={(e) => {
                  const newIdx = e.target.value;

                  // Save valid buffered changes before switching
                  // Invalid data stays buffered and will be shown again if user returns to that index
                  if (this.state.isDirty) {
                    const currentErrors = this.state.validationErrors[configSelectionIdx] || {};
                    const hasErrors = Object.keys(currentErrors).length > 0;

                    if (!hasErrors) {
                      this.propagateToParent();
                    }
                    // If there are errors, keep buffered state and just switch index
                    // This preserves valid changes at other indices
                  }

                  // Switch to new config selection
                  this.setState({ configSelectionIdx: newIdx });
                }}
              >
                {
                  this.state.options.map((option, idx) => (
                    <MenuItem value={idx} key={idx}>{ `Option ${idx + 1}. ${option.text}` }  </MenuItem>
                  ))
                }
              </Select><br/><br/>
            </>
          )
        }
        {
          configData.inputs[configSelectionIdx] &&
          configData.inputs[configSelectionIdx].isCustom &&
          configData.inputType && LikertInputTypes[configData.inputType] &&
          LikertInputTypes[configData.inputType].optionValuesConfig &&
          LikertInputTypes[configData.inputType].optionValuesConfig.indexOf('custom') !== -1 && (
          configData.inputs[configSelectionIdx].custom.map((option, index) => {
              return (
                <div
                  className="translation-line"
                  style={{ display: "flex", alignItems: "flex-start", marginBottom: "4px" }}
                  key={index}
                >
                  <TextField
                    value={option.label}
                    variant="outlined"
                    margin="dense"
                    className="translation-flex custom-likert-label-input"
                    placeholder="Value"
                    onChange={(e) => {
                      const cData = this.cloneConfigData();
                      cData.inputs[configSelectionIdx]['custom'][index]['label'] = e.target.value;
                      this.updateBufferedState(cData);
                    }}
                  />
                  <div className="selection-options-updown add-translation-lang">
                    <FontAwesomeIcon
                      icon={faPlusSquare}
                      className="add-response"
                      onClick={() => {
                        const cData = this.cloneConfigData();
                        const customItems = cData.inputs[configSelectionIdx].custom;
                        const newIndex = customItems.length + 1;

                        customItems.push({
                          label: `Option ${newIndex}`,
                          value: this.generateUniqueValue(customItems)
                        });

                        this.updateBufferedState(cData);
                      }}
                    />
                    <FontAwesomeIcon
                      icon={faMinusSquare}
                      className="add-response"
                      onClick={() => {
                        if (configData.inputs[configSelectionIdx].custom.length <= 1) return;

                        const cData = this.cloneConfigData();
                        cData.inputs[configSelectionIdx]['custom'] = removeItemFromArray(
                          configData.inputs[configSelectionIdx].custom,
                          index
                        );

                        this.updateBufferedState(cData);
                      }}
                    />
                  </div>
                  <Tooltip
                    title={currentErrors[index] || ""}
                    placement="top"
                    arrow
                    classes={{ popper: currentErrors[index] ? 'error-tooltip' : '' }}
                  >
                    <TextField
                        type="number"
                        value={option.value || ''}
                        variant="outlined"
                        margin="dense"
                        className={`translation-flex recode-likert-label-input ${currentErrors[index] ? 'error-field' : ''}`}
                        placeholder="Recode #"
                        error={!!currentErrors[index]}
                        style={{ minWidth: "120px" }}
                        onChange={(e) => {
                          const cData = this.cloneConfigData();
                          cData.inputs[configSelectionIdx]['custom'][index]['value'] = e.target.value;
                          this.updateBufferedState(cData);
                        }}
                    />
                  </Tooltip>
                </div>
              )
            })
          )
        }
        {
          configData.inputs[configSelectionIdx] &&
          configData.inputs[configSelectionIdx].isCustom &&
          hasErrors && (
            <div className="likert-validation-alert">
              <span>⚠️ Some custom values have errors and won't be saved</span>
            </div>
          )
        }
        {
          configData.inputType && LikertInputTypes[configData.inputType] &&
          LikertInputTypes[configData.inputType].optionValuesConfig &&
          LikertInputTypes[configData.inputType].optionValuesConfig.indexOf('bounds') !== -1 && (
            <>
              <TextField
                variant="outlined"
                margin="dense"
                fullWidth
                label="Lower Bound"
                type='number'
                value={configData.inputs[configSelectionIdx].bounds.lower.value}
                onChange={(e) => {
                  const cData = this.cloneConfigData();
                  cData.inputs[configSelectionIdx]['bounds']['lower']['value'] = Number(e.target.value);
                  this.saveAndClearBuffer(cData);
                }}
                style={{ marginBottom: 16, marginTop: 0 }}
              />
              <TextField
                variant="outlined"
                margin="dense"
                fullWidth
                label="Lower Bound Label"
                value={configData.inputs[configSelectionIdx].bounds.lower.label}
                onChange={(e) => {
                  const cData = this.cloneConfigData();
                  cData.inputs[configSelectionIdx]['bounds']['lower']['label'] = e.target.value;
                  this.saveAndClearBuffer(cData);
                }}
                style={{ marginBottom: 16, marginTop: 0 }}
              />
              <TextField
                variant="outlined"
                margin="dense"
                fullWidth
                label="Upper Bound"
                type='number'
                value={configData.inputs[configSelectionIdx].bounds.upper.value}
                onChange={(e) => {
                  const cData = this.cloneConfigData();
                  cData.inputs[configSelectionIdx]['bounds']['upper']['value'] = Number(e.target.value);
                  this.saveAndClearBuffer(cData);
                }}
                style={{ marginBottom: 16, marginTop: 0 }}
              />
              <TextField
                variant="outlined"
                margin="dense"
                fullWidth
                label="Upper Bound Label"
                value={configData.inputs[configSelectionIdx].bounds.upper.label}
                onChange={(e) => {
                  const cData = this.cloneConfigData();
                  cData.inputs[configSelectionIdx]['bounds']['upper']['label'] = e.target.value;
                  this.saveAndClearBuffer(cData);
                }}
                style={{ marginBottom: 16, marginTop: 0 }}
              />
              <TextField
                variant="outlined"
                margin="dense"
                fullWidth
                label="Interval"
                type='number'
                value={configData.inputs[configSelectionIdx].bounds.interval}
                onChange={(e) => {
                  const cData = this.cloneConfigData();
                  cData.inputs[configSelectionIdx]['bounds']['interval'] = Number(e.target.value);
                  this.saveAndClearBuffer(cData);
                }}
                style={{ marginBottom: 16, marginTop: 0 }}
              />
            </>
          )
        }
      </>
    );
  }
}